import {isAbsenceCodeBlocked} from "./absenceRestriction";

/**
 * Quick-entry favorite templates (JIRA ID + summary + category + comment) used to
 * add extra rows in the Copy/Edit dialogs. Managed on the Preferences page and
 * stored server-side as `preferences.weekActionsFavorites` — NOT the same concept
 * as userSlice's `currentFavs` (favourite people for tabs).
 */
export interface FavoriteEntry {
    jiraId: string;
    summary: string;
    category: string;
    comment: string;
}

export const DEFAULT_CATEGORY = "AD";

export const DEFAULT_FAVORITES: FavoriteEntry[] = [
    {jiraId: "NBP-4", summary: "AD Indirect Hours", category: DEFAULT_CATEGORY, comment: "Mentoring"},
    {jiraId: "NBP-4", summary: "AD Indirect Hours", category: DEFAULT_CATEGORY, comment: "Training"},
    {jiraId: "NBP-4", summary: "AD Indirect Hours", category: DEFAULT_CATEGORY, comment: "Travel"},
    {jiraId: "NBP-14", summary: "AD Absence Hours", category: DEFAULT_CATEGORY, comment: "Holiday"},
    {jiraId: "NBP-14", summary: "AD Absence Hours", category: DEFAULT_CATEGORY, comment: "Public Holiday"},
    {jiraId: "NBP-14", summary: "AD Absence Hours", category: DEFAULT_CATEGORY, comment: "Sick Leave"},
    {jiraId: "NBP-5", summary: "AD ASML Sales activities 2026", category: DEFAULT_CATEGORY, comment: "Sales Activity:"}
];

function normalizeEntry(entry: Partial<FavoriteEntry> | null | undefined): FavoriteEntry {
    return {
        jiraId: String(entry?.jiraId || "").trim(),
        summary: String(entry?.summary || "").trim(),
        category: String(entry?.category || DEFAULT_CATEGORY).trim() || DEFAULT_CATEGORY,
        comment: String(entry?.comment || "").trim()
    };
}

/** Strips blank rows and normalizes fields; used by the Preferences page before saving. */
export function cleanFavoritesList(list: Partial<FavoriteEntry>[]): FavoriteEntry[] {
    return (Array.isArray(list) ? list : [])
        .map(normalizeEntry)
        .filter((entry) => entry.jiraId.length > 0);
}

// In-memory mirror of the backend-stored `preferences.weekActionsFavorites`, kept
// fresh by TimeTable.tsx whenever Redux preferences load/change. Replaces the old
// localStorage-backed store now that favorites live in user preferences. `null`
// means "not synced from the backend yet" — a brand-new user's list is seeded
// server-side with DEFAULT_FAVORITES, so an empty (but synced) list here means
// the user deliberately cleared their favorites and must stay empty.
let favoritesCache: FavoriteEntry[] | null = null;

export function syncFavoritesFromPreferences(list: FavoriteEntry[] | undefined | null): void {
    favoritesCache = Array.isArray(list) ? cleanFavoritesList(list) : null;
}

/**
 * Favorites for the current user, filtered for restricted absence codes. Only
 * falls back to DEFAULT_FAVORITES in the brief window before the backend
 * preferences have synced at all.
 */
export function loadFavorites(currentUserName: string): FavoriteEntry[] {
    const list = favoritesCache ?? DEFAULT_FAVORITES;
    return list.filter((entry) => !isAbsenceCodeBlocked(entry.jiraId, currentUserName));
}

